import { useEffect } from "react";

import {
  BrowserRouter,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";

import "./App.css";

// Components
import CategoryPage from "./components/CategoryPage.js";
import EndUserSignIn from "./components/enduser/EndUserSignIn";
import EndUserSignUp from "./components/enduser/EndUserSignUp";
import Layout from "./components/Layout";
import MemberSignIn from "./components/member/MemberSignIn";
import MemberSignUp from "./components/member/MemberSignUp";
import ChannelPartnerSignUp from "./components/partner/ChannelPartnerSignUp";
import SubCategoryPage from "./components/SubCategoryPage.js";

// Context
import { AuthProvider } from "./context.js";
import { CurrencyProvider } from "./features/context/CurrencyContext.js";

// Pages
import About from "./pages/About";
import Cart from "./pages/Cart";
import ChannelPartner from "./pages/ChannelPartner.js";
import Checkout from "./pages/Checkout";
import CompanyRefer from "./pages/CompanyRefer.js";
import Contact from "./pages/Contact";
import CustomerSpeak from "./pages/CustomerSpeak.js";
import EditorialPolicy from "./pages/EditorialPolicy.js";
import FaQ from "./pages/FaQ.js";
import FooterPartner from "./pages/FooterPartner.js";
import Forgotpassword from "./pages/Forgotpassword";
import Forum from "./pages/Forum.js";
import Home from "./pages/Home";
import Login from "./pages/Login";
import OrderHistory from "./pages/orderHistory.js";
import Orders from "./pages/Orders";
import PaymentPolicy from "./pages/PaymentPolicy.js";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import ProductDetails from "./pages/ProductDetails";
import CategoryProductsPage from "./pages/ProductsPage";
import Profile from "./pages/Profile";
import ReferAndEarn from "./pages/ReferAndEarn.js";
import RefundPolicy from "./pages/RefundPolicy";
import Resetpassword from "./pages/Resetpassword";
import ShippingPolicy from "./pages/ShippingPolicy";

// New Pages
import AIServices from "./pages/AIServices";
import ArteryVascularAge from "./pages/ArteryVascularAge";
import AntioxidantMeasurementTest from "./pages/AntioxidantMeasurementTest";
import MarathonRegistration from "./pages/MarathonRegistration";

// Medical Devices
// Temporarily disabled because these files may be incomplete.
// import MedicalDevices from "./pages/MedicalDevices/MedicalDevices";
// import DeviceAccessories from "./pages/MedicalDevices/DeviceAccessories";
// import Diagnostics from "./pages/MedicalDevices/Diagnostics";
// import Endoscopy from "./pages/MedicalDevices/Endoscopy";
// import HospitalConsumables from "./pages/MedicalDevices/HospitalConsumables";
// import HospitalEquipment from "./pages/MedicalDevices/HospitalEquipment";
// import SleepApnea from "./pages/MedicalDevices/SleepApnea";
// import SleepApneaDetail from "./pages/MedicalDevices/SleepApneaDetail";

// Other Pages
import Signup from "./pages/Signup";
import TermAndContions from "./pages/TermAndContions";
import VendorPolicy from "./pages/VendorPolicy.js";
import Wishlist from "./pages/Wishlist";

// Routing
import { OpenRoutes } from "./routing/OpenRoutes";
import { PrivateRoutes } from "./routing/PrivateRoutes";

// Partner / Signup Components
import Repayment from "./components/partner/Repayment.js";
import CorporateSignup from "./components/corporate/corporateSignup.js";
import DirectorSignUp from "./components/director/directorSignup.js";
import VendorSignup from "./components/vendor/vendorSignup.js";
import VendorProductDetails from "./pages/vendorProductDetails.js";
import FranchiseSignUp from "./components/franchise/franchiseSignup.js";
import RetailerOrder from "./pages/retailerOrder.js";

// Services / Content
import ServicesPage from "./components/services.js";
import ServiceDetailsClient from "./components/serviceData.js";
import NewsEvents from "./components/news.js";
import Gallery from "./components/gallery.js";
import PressMedia from "./components/press.js";
import BlogsPage from "./components/blogs.js";
import NewsEventDetail from "./components/newsEvents.js";
import BlogDetail from "./components/blogDetails.js";
import Shop from "./pages/Shop.js";
import Testimonials from "./pages/Testimonials.js";
import InvestorRelationsPage from "./components/investor.js/investorSignup.js";
import EmergencyProfile from "./pages/EmergencyProfile.js";
import FirstVisitModal from "./pages/FirstModalShow.js";

// Scroll To Top
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

// Modal Wrapper
const ModalWrapper = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const handleClose = () => {
    const from = location.state?.from;

    if (from) {
      navigate(from, { replace: true });
    } else {
      navigate(-1);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="relative bg-white rounded-lg shadow-lg modal-90w p-4">
        {children({ onHide: handleClose })}
      </div>
    </div>
  );
};

// Channel Partner Redirect
const ChannelPartnerSignInRedirect = () => {
  useEffect(() => {
    window.location.href = "https://lifeonplus.in/dashboardPanel/partnerLogin";
  }, []);

  return null;
};

// App
function App() {
  const hideModal = window.location.pathname.startsWith("/register/");

  return (
    <AuthProvider>
      <CurrencyProvider>
        <BrowserRouter basename="/">
          <ScrollToTop />

          <Routes>
            {/* Main Layout */}
            <Route path="/" element={<Layout />}>
              <Route index element={<Home />} />

              {/* Categories */}
              <Route path="category/:categoryName" element={<CategoryPage />} />

              <Route
                path="category/:categoryName/:subCategoryName"
                element={<CategoryPage />}
              />

              <Route
                path="category/:categoryName/:subCategoryName/:subSubCategoryName"
                element={<CategoryPage />}
              />

              <Route
                path="category/:categoryName/:subCategoryName/:subSubCategoryName/:subSubSubCategoryName"
                element={<CategoryPage />}
              />

              <Route
                path="category/:categoryName/:subCategoryName/products"
                element={<SubCategoryPage />}
              />

              <Route
                path="category/:categoryName/:subCategoryName/:subSubCategoryName/products"
                element={<SubCategoryPage />}
              />

              <Route
                path="category/:categoryName/:subCategoryName/:subSubCategoryName/:subSubSubCategoryName/products"
                element={<SubCategoryPage />}
              />

              {/* Medical Devices - Temporarily Disabled */}
              {/*
              <Route path="medical-devices" element={<MedicalDevices />} />
              <Route path="medical-devices/device-accessories" element={<DeviceAccessories />} />
              <Route path="medical-devices/diagnostics" element={<Diagnostics />} />
              <Route path="medical-devices/endoscopy" element={<Endoscopy />} />
              <Route path="medical-devices/hospital-consumables" element={<HospitalConsumables />} />
              <Route path="medical-devices/hospital-equipment" element={<HospitalEquipment />} />
              <Route path="medical-devices/sleep-apnea" element={<SleepApnea />} />
              <Route path="medical-devices/sleep-apnea/:id" element={<SleepApneaDetail />} />
              */}

              {/* Shop */}
              <Route path="shop" element={<Shop />} />

              <Route path="categories/:id" element={<CategoryProductsPage />} />

              <Route path="product-details/:id" element={<ProductDetails />} />

              <Route
                path="vendor-product-details/:id"
                element={<VendorProductDetails />}
              />

              {/* Services */}
              <Route path="services" element={<ServicesPage />} />

              <Route path="services/:id" element={<ServiceDetailsClient />} />

              {/* News & Blog */}
              <Route path="news-events" element={<NewsEvents />} />

              <Route path="news-events/:id" element={<NewsEventDetail />} />

              <Route path="blog" element={<BlogsPage />} />
              <Route path="blog/:id" element={<BlogDetail />} />
              <Route path="press-media" element={<PressMedia />} />
              <Route path="gallery" element={<Gallery />} />

              {/* AI Services */}
              <Route path="ai-services" element={<AIServices />} />

              {/* Artery Vascular Age */}
              <Route
                path="artery-vascular-age"
                element={<ArteryVascularAge />}
              />

              {/* Antioxidant Measurement Test */}
              <Route
                path="antioxidant-measurement-test"
                element={<AntioxidantMeasurementTest />}
              />

              {/* Company */}
              <Route path="about" element={<About />} />

              <Route path="testimonial-page" element={<Testimonials />} />

              <Route path="contact" element={<Contact />} />
              <Route path="faq" element={<FaQ />} />

              <Route path="what-do-customer-say" element={<CustomerSpeak />} />

              <Route path="forum" element={<Forum />} />

              <Route path="company-refer" element={<CompanyRefer />} />

              <Route path="channel-partner" element={<ChannelPartner />} />

              <Route path="partner-model" element={<FooterPartner />} />

              <Route path="refer" element={<ReferAndEarn />} />

              {/* User */}
              <Route path="cart" element={<Cart />} />

              <Route path="order-history" element={<OrderHistory />} />

              <Route path="profile" element={<Profile />} />

              <Route
                path="my-orders"
                element={
                  <PrivateRoutes>
                    <Orders />
                  </PrivateRoutes>
                }
              />

              <Route
                path="checkout"
                element={
                  <PrivateRoutes>
                    <Checkout />
                  </PrivateRoutes>
                }
              />

              <Route
                path="wishlist"
                element={
                  <PrivateRoutes>
                    <Wishlist />
                  </PrivateRoutes>
                }
              />

              {/* Password */}
              <Route path="forgot-password" element={<Forgotpassword />} />

              <Route path="reset-password/:token" element={<Resetpassword />} />

              {/* Policies */}
              <Route path="privacy-policy" element={<PrivacyPolicy />} />

              <Route path="fees-and-payment" element={<PaymentPolicy />} />

              <Route path="refund-policy" element={<RefundPolicy />} />

              <Route path="shipping-policy" element={<ShippingPolicy />} />

              <Route path="term-conditions" element={<TermAndContions />} />

              <Route path="editorial-policy" element={<EditorialPolicy />} />

              <Route path="vendor-policy" element={<VendorPolicy />} />

              {/* Other */}
              <Route path="repayment/:id" element={<Repayment />} />

              {/* Member Sign In */}
              <Route
                path="signin/member"
                element={
                  <OpenRoutes>
                    <ModalWrapper>
                      {({ onHide }) => (
                        <MemberSignIn show={true} onHide={onHide} />
                      )}
                    </ModalWrapper>
                  </OpenRoutes>
                }
              />

              {/* End User Sign In */}
              <Route
                path="signin/enduser"
                element={
                  <OpenRoutes>
                    <ModalWrapper>
                      {({ onHide }) => (
                        <EndUserSignIn show={true} onHide={onHide} />
                      )}
                    </ModalWrapper>
                  </OpenRoutes>
                }
              />

              {/* Channel Partner Sign In */}
              <Route
                path="signin/channelpartner"
                element={
                  <OpenRoutes>
                    <ChannelPartnerSignInRedirect />
                  </OpenRoutes>
                }
              />

              {/* Member Sign Up */}
              <Route
                path="signup/member"
                element={
                  <OpenRoutes>
                    <ModalWrapper>
                      {({ onHide }) => (
                        <MemberSignUp show={true} onHide={onHide} />
                      )}
                    </ModalWrapper>
                  </OpenRoutes>
                }
              />

              {/* End User Sign Up */}
              <Route
                path="signup/enduser"
                element={
                  <OpenRoutes>
                    <ModalWrapper>
                      {({ onHide }) => (
                        <EndUserSignUp show={true} onHide={onHide} />
                      )}
                    </ModalWrapper>
                  </OpenRoutes>
                }
              />

              {/* Corporate Sign Up */}
              <Route
                path="signup/corporate"
                element={
                  <OpenRoutes>
                    <ModalWrapper>
                      {({ onHide }) => (
                        <CorporateSignup show={true} onHide={onHide} />
                      )}
                    </ModalWrapper>
                  </OpenRoutes>
                }
              />

              {/* Director Sign Up */}
              <Route
                path="signup/director"
                element={
                  <OpenRoutes>
                    <ModalWrapper>
                      {({ onHide }) => (
                        <DirectorSignUp show={true} onHide={onHide} />
                      )}
                    </ModalWrapper>
                  </OpenRoutes>
                }
              />

              {/* Channel Partner Sign Up */}
              <Route
                path="signup/channelpartner"
                element={
                  <OpenRoutes>
                    <ModalWrapper>
                      {({ onHide }) => (
                        <ChannelPartnerSignUp show={true} onHide={onHide} />
                      )}
                    </ModalWrapper>
                  </OpenRoutes>
                }
              />

              {/* Channel Partner Plus */}
              <Route
                path="signup/channelpartnerplus"
                element={
                  <OpenRoutes>
                    <ModalWrapper>
                      {({ onHide }) => (
                        <ChannelPartnerSignUp show={true} onHide={onHide} />
                      )}
                    </ModalWrapper>
                  </OpenRoutes>
                }
              />

              {/* Vendor Sign Up */}
              <Route
                path="signup/vendor"
                element={
                  <OpenRoutes>
                    <ModalWrapper>
                      {({ onHide }) => (
                        <VendorSignup show={true} onHide={onHide} />
                      )}
                    </ModalWrapper>
                  </OpenRoutes>
                }
              />

              {/* Franchise Sign Up */}
              <Route
                path="signup/franchise"
                element={
                  <OpenRoutes>
                    <ModalWrapper>
                      {({ onHide }) => (
                        <FranchiseSignUp show={true} onHide={onHide} />
                      )}
                    </ModalWrapper>
                  </OpenRoutes>
                }
              />

              {/* Investor Sign Up */}
              <Route
                path="signup/investor"
                element={
                  <OpenRoutes>
                    <ModalWrapper>
                      {({ onHide }) => (
                        <InvestorRelationsPage show={true} onHide={onHide} />
                      )}
                    </ModalWrapper>
                  </OpenRoutes>
                }
              />
            </Route>

            {/* Normal Login */}
            <Route
              path="login"
              element={
                <OpenRoutes>
                  <Login />
                </OpenRoutes>
              }
            />

            {/* Normal Signup */}
            <Route
              path="signup"
              element={
                <OpenRoutes>
                  <Signup />
                </OpenRoutes>
              }
            />

            {/* Retailer */}
            <Route path="register/:id" element={<RetailerOrder />} />

            {/* Emergency Profile */}
            <Route path="emergency/:token" element={<EmergencyProfile />} />

            <Route
              path="marathon-registration"
              element={<MarathonRegistration />}
            />
          </Routes>

          {/* Temporarily disabled old first visit modal */}
          {!hideModal && <FirstVisitModal />}
        </BrowserRouter>
      </CurrencyProvider>
    </AuthProvider>
  );
}

export default App;
